import type { Container } from 'pixi.js';

/**
 * On-screen diagnostics for the showcase (`?debug=1&probe=1`): ticker liveness, game state, mascot layer state
 * and its event trace, refreshed every second in a fixed overlay. Meant for checking a real device where no
 * console is at hand (see public/diag.html for the standalone variant).
 */
interface DebugHandle {
  presenter: { app: { ticker: { lastTime: number; FPS: number; _requestId: number | null }; stage: Container; renderer: { extract: { pixels(target: Container): { pixels: Uint8ClampedArray | Uint8Array } } } } };
  game: { snapshot: { state: string } };
}
interface MascotDebug extends Container {
  live?: boolean;
  shown?: boolean;
  frames?: number;
  box?: unknown;
  videos?: Record<string, HTMLVideoElement>;
  canvases?: Record<string, { ctx: CanvasRenderingContext2D; drawnTime: number }>;
  sprites?: Record<string, Container>;
  trace?: string[];
}

/** Cheap fingerprint of pixel data (every 331st pixel) to tell whether content changed. */
function fingerprint(d: Uint8ClampedArray | Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < d.length; i += 4 * 331) sum += d[i] + d[i + 1] * 3 + d[i + 2] * 7 + d[i + 3] * 11;
  return sum;
}

export function startProbe(mount: HTMLElement): void {
  const el = document.createElement('pre');
  Object.assign(el.style, {
    position: 'fixed', left: '0', bottom: '0', zIndex: '100000', margin: '0', padding: '4px 6px', maxWidth: '100vw', maxHeight: '40vh',
    overflow: 'hidden', background: 'rgba(0,0,0,.82)', color: '#9f9', font: '10px/1.3 Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', pointerEvents: 'none',
  });
  document.body.appendChild(el);
  let lastTick = -1;
  const autospin = new URLSearchParams(location.search).get('autospin') === '1';
  let spun = false;
  const t0 = performance.now();
  const errors: string[] = [];
  window.addEventListener('error', (e) => errors.push(`${e.message} @${(e.filename || '').split('/').pop()}:${e.lineno}`));
  window.addEventListener('unhandledrejection', (e) => errors.push(`rejection: ${String((e.reason as Error)?.message ?? e.reason)}`));
  const prints: Record<string, number> = {};
  const mark = (key: string, fp: number): string => {
    const r = key in prints ? (prints[key] === fp ? ' SAME as 1s ago' : ' changed') : '';
    prints[key] = fp;
    return `fp=${fp}${r}`;
  };
  const find = (c: Container): MascotDebug | null => {
    if (c.label === 'mascot') return c as MascotDebug;
    for (const ch of c.children) {
      const r = find(ch as Container);
      if (r) return r;
    }
    return null;
  };
  setInterval(() => {
    const dbg = (mount as HTMLElement & { __slotDebug?: DebugHandle }).__slotDebug;
    if (!dbg) {
      el.textContent = 'probe: slot not ready';
      return;
    }
    if (autospin && !spun && performance.now() - t0 > 9000) {
      spun = true;
      (dbg.game as unknown as { forceNext(f: { tier: string }): void; spin(): Promise<void> }).forceNext({ tier: 'big' });
      void (dbg.game as unknown as { spin(): Promise<void> }).spin();
    }
    const tk = dbg.presenter.app.ticker;
    const alive = tk.lastTime !== lastTick;
    lastTick = tk.lastTime;
    const lines = [`ticker ${alive ? 'alive' : 'DEAD'} last=${Math.round(tk.lastTime)} fps=${tk.FPS.toFixed(0)} req=${String(tk._requestId)} | state=${dbg.game.snapshot.state} | ${innerWidth}x${innerHeight} | now=${Math.round(performance.now())}`];
    lines.push(`errors: ${errors.length ? errors.slice(-2).join(' || ') : 'none'}`);
    const m = ((dbg.presenter as unknown as { mascot?: MascotDebug }).mascot ?? find(dbg.presenter.app.stage)) as MascotDebug | null;
    if (m) {
      lines.push(`mascot ${(m as { el?: HTMLElement }).el ? 'DOM' : 'pixi'} live=${String(m.live)} shown=${String(m.shown)} presented=${String((m as { presented?: number }).presented)} box=${JSON.stringify(m.box)}`);
      for (const [k, v] of Object.entries(m.videos ?? {})) {
        let extra = '';
        const c = m.canvases?.[k];
        if (c) extra += ` | canvas t=${c.drawnTime.toFixed(2)} ${mark('c:' + k, fingerprint(c.ctx.getImageData(0, 0, c.ctx.canvas.width, c.ctx.canvas.height).data))}`;
        lines.push(`${k}: rs=${v.readyState} ${v.paused ? 'paused' : 'playing'} t=${v.currentTime.toFixed(2)}${extra}`);
      }
      lines.push((m.trace ?? []).slice(-7).join(' | '));
    } else lines.push('mascot layer: none');
    el.textContent = lines.join('\n');
  }, 1000);
}
